import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { getCurrentStory } from "@/data/happeningToday";
import { hasStoryPageData } from "@/data/happeningStories";
import { useStoryHero } from "@/hooks/useStoryHero";
import type { HappeningStoryRow } from "@/lib/happening-stories-api";

const msUntilNextMidnight = () => {
  const now = new Date();
  const next = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    0,
    0,
    0,
    0,
  );
  return next.getTime() - now.getTime();
};

const HappeningTodaySection = () => {
  const [story, setStory] = useState(() => getCurrentStory());
  const [dbRow, setDbRow] = useState<HappeningStoryRow | null>(null);
  const { override: heroOverride } = useStoryHero(dbRow?.slug ?? story.id);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const refresh = () => setStory(getCurrentStory(new Date()));

    const timeoutId = setTimeout(() => {
      refresh();
      intervalId = setInterval(refresh, 24 * 60 * 60 * 1000);
    }, msUntilNextMidnight() + 1000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    let active = true;
    import("@/lib/happening-stories-api").then((m) => m.getCurrentHappeningStory()).then((row) => {
      if (active) setDbRow(row);
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  const active = dbRow
    ? {
        id: dbRow.slug,
        badge: dbRow.badge,
        title: dbRow.title,
        body: dbRow.intro,
        date: dbRow.meta?.date_window || "Updated for the coast today",
        location: dbRow.meta?.location || "San Diego coast",
        image: dbRow.hero_image_url || story.image,
        imageAlt: dbRow.hero_alt || story.imageAlt,
        imageWidth: story.imageWidth,
        imageHeight: story.imageHeight,
        sources: undefined,
        href: `/happening/${dbRow.slug}`,
      }
    : {
        ...story,
        href: story.pageHref && hasStoryPageData(story.id) ? story.pageHref : story.cta.to,
      };

  return (
    <section className="bg-background py-6 px-4 sm:px-6">
      {(() => {
        const href = active.href;
        const isExternal = !dbRow && story.cta.external;
        const cardInner = (
          <div className="relative md:flex">
            {/* Image */}
            <div className="relative h-56 md:h-auto md:w-1/2 lg:w-[55%]">
              <picture>
                {active.sources && !heroOverride && (
                  <>
                    <source
                      type="image/avif"
                      srcSet={`${active.sources.avif.w640} 640w, ${active.sources.avif.w1024} 1024w, ${active.sources.avif.w1600} 1600w`}
                      sizes="(min-width: 768px) 55vw, 100vw"
                    />
                    <source
                      type="image/webp"
                      srcSet={`${active.sources.webp.w640} 640w, ${active.sources.webp.w1024} 1024w, ${active.sources.webp.w1600} 1600w`}
                      sizes="(min-width: 768px) 55vw, 100vw"
                    />
                  </>
                )}
                <img
                  src={heroOverride?.url ?? active.image}
                  alt={heroOverride?.alt || active.imageAlt}
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                  width={active.imageWidth}
                  height={active.imageHeight}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/20" />
            </div>

            {/* Content */}
            <div className="relative flex flex-col justify-center gap-4 bg-card p-6 sm:p-8 md:w-1/2 lg:w-[45%]">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
                </span>
                <span className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-[hsl(0_72%_38%)] dark:text-[hsl(0_85%_72%)]">
                  {active.badge}
                </span>
              </div>

              <h2 className="font-display text-2xl font-semibold leading-tight text-foreground sm:text-3xl group-hover:text-primary transition-colors">
                {active.title}
              </h2>

              <p className="font-body text-sm leading-relaxed text-muted-foreground">
                {active.body}
              </p>

              <div className="flex flex-wrap gap-4 text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 font-body text-xs">
                  <Clock className="h-3.5 w-3.5 text-primary" />
                  {active.date}
                </span>
                <span className="inline-flex items-center gap-1.5 font-body text-xs">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  {active.location}
                </span>
              </div>

              <span className="inline-flex items-center gap-1.5 font-body text-sm font-semibold text-primary">
                Read today's briefing
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </div>
        );

        const wrapperClass =
          "group block mx-auto max-w-5xl overflow-hidden rounded-2xl border border-border shadow-lg animate-fade-in transition-all hover:shadow-xl hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

        return isExternal ? (
          <a href={href} className={wrapperClass} target="_blank" rel="noopener noreferrer">
            {cardInner}
          </a>
        ) : (
          <Link to={href} className={wrapperClass} aria-label={active.title}>
            {cardInner}
          </Link>
        );
      })()}
    </section>
  );
};

export default HappeningTodaySection;
